import {
  put, takeLatest, select, call
} from 'redux-saga/effects';

import {
  MODALS, FETCH_OPTIONS,
  API_URLS,
} from '../constants';
import { aSetIsFetching, aSetOpenModal } from '../reducers/common';
import { aExportRoundSpecialOddsDetailsAction, aGetRoundSpecialDetailsAction, aGetRoundSpecialDetailsFailAction, aGetRoundSpecialDetailsSuccessAction, aGetRoundSpecialOddsDetailsAction, aGetRoundSpecialOddsDetailsSuccessAction, aGetRoundSpecialRoundsAction, aGetRoundSpecialRoundsSuccessAction } from '../reducers/roundSpecials';
import { getRoundSpecialSelectedMarketHistoryOdds, getRoundSpecialsRounds } from '../selectors/roundSpecials';
import { requestGet, requestGetDownloadedFile } from '../utils/request';

/**
 * group historical events based on the startdate hour
 *
 * @param {object} rounds
 * @returns {object[]} array of objects that has the hour and events for that hour
 *
 * ex. [
    {
      hour: 1:00,
      rounds: { //data }
    }
 * ]
 */
function groupRoundsByHour(rounds) {
  const roundsCopy = [...rounds];

  const roundsByHourObject = roundsCopy
    .reduce((acc, curr) => {
      const [date, timeUTC] = curr.startDateTime.split(' ');
      const dateWithTimezoneOfUser = new Date(`${date}T${timeUTC}Z`);
      const hour = dateWithTimezoneOfUser.getHours();
      // eslint-disable-next-line no-unused-vars
      const [year, month, day] = date.split('-');
      const key = `${month}-${day}:${hour}`;

      if (acc[key] === undefined) {
        const paddedHour = `${hour}`.padStart(2, '0');
        acc[key] = {
          hour: `${paddedHour}:00`,
          rounds: [curr],
        };

        return acc;
      }

      acc[key].rounds.push(curr);
      return acc;
    }, {});

  return Object.keys(roundsByHourObject).map((key) => ({
    hour: roundsByHourObject[key].hour,
    rounds: roundsByHourObject[key].rounds,
  }));
}
/**
 *
 * @param action
 * @memberof sagas/roundSpecials
 * @async
 * @returns {void}
 */
function* getRoundSpecialsRoundSaga(action) {
  try {
    yield put(aSetIsFetching({
      key: FETCH_OPTIONS.GET_ROUND_SPECIALS,
      value: true,
    }));

    const response = yield call(
      requestGet,
      action.payload?.queryParams ? `${API_URLS.GET_ROUND_SPECIAL_ROUNDS}${action.payload.queryParams}` : API_URLS.GET_ROUND_SPECIAL_ROUNDS,
      true,
    );

    if (response.status === -1) {
      throw Error(response.error.message);
    }

    const previousRounds = yield select(getRoundSpecialsRounds);
    const groupedRounds = groupRoundsByHour(response.data.rounds);

    const rounds = action.payload?.loadMore ? [...previousRounds, ...groupedRounds] : groupedRounds;

    yield put(aSetIsFetching({
      key: FETCH_OPTIONS.GET_ROUND_SPECIALS,
      value: false,
    }));

    yield put(aGetRoundSpecialRoundsSuccessAction({
      rounds,
      lastRoundId: response.data?.lastRoundId,
      lastDate: response.data?.lastDate,
    }));
  } catch (error) {
    yield put(aSetOpenModal({
      modal: error.modal || MODALS.GENERAL_ERROR,
      errorMessage: error.modal ? error.message : MODALS.GENERAL_ERROR,
      key: FETCH_OPTIONS.GET_ROUND_SPECIALS,
      value: false,
    }));
  }
}

/**
 *
 * @param action
 * @memberof sagas/roundSpecials
 * @async
 * @returns {void}
 */
function* getRoundSpecialDetailsSaga(action) {
  try {
    yield put(aSetIsFetching({
      key: FETCH_OPTIONS.GET_ROUND_SPECIALS_TIMELINE,
      value: true,
    }));

    const response = yield call(
      requestGet,
      `${API_URLS.GET_ROUND_SPECIAL_TIMELINE}?roundId=${action.payload.id}`,
      true,
    );

    if (response.status === -1) {
      throw Error(response.error.message);
    }

    yield put(aGetRoundSpecialDetailsSuccessAction({ details: response.data.roundDetails, timeline: response.data.roundTimeline, cornerTimeline: response.data.roundCornerTimeline }));

    yield put(aSetIsFetching({
      key: FETCH_OPTIONS.GET_ROUND_SPECIALS_TIMELINE,
      value: false,
    }));
  } catch (error) {
    yield put(aGetRoundSpecialDetailsFailAction());
    yield put(aSetOpenModal({
      modal: error.modal || MODALS.GENERAL_ERROR,
      errorMessage: error.modal ? error.message : MODALS.GENERAL_ERROR,
      key: FETCH_OPTIONS.GET_ROUND_SPECIALS_TIMELINE,
      value: false,
    }));
  }
}

async function downloadFile(apiResponse, filename) {
  const blob = await apiResponse.blob();

  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.style.display = 'none';
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
}

/**
 *
 * @param action
 * @memberof sagas/roundSpecials
 * @async
 * @returns {void}
 */
function* exportExcelRoundSpecial(action) {
  try {
    yield put(aSetIsFetching({
      key: FETCH_OPTIONS.EXPORT_ROUND_SPECIALS_ODDS,
      value: true,
    }));

    const markets = action.payload.markets.map(marketId => `markets[]=${marketId}`).join('&');
    const response = yield call(
      requestGetDownloadedFile,
      `${API_URLS.GET_ROUND_SPECIAL_EXCEL}?roundId=${action.payload.roundId}&${markets}`,
      true,
    );

    if (response.status === -1) {
      throw Error(response.error.message);
    }
    const contentDispositionHeader = response.headers.get('content-disposition');
    const filenameKeyValueString = contentDispositionHeader.split(';').find(item => item.includes('filename='));
    
    if (!filenameKeyValueString) {
      throw Error('There is no filename.');
    }
    
    // eslint-disable-next-line no-unused-vars
    const [key, filename] = filenameKeyValueString.split('=');

    yield call(downloadFile, response, filename);
    
    yield put(aSetIsFetching({
      key: FETCH_OPTIONS.EXPORT_ROUND_SPECIALS_ODDS,
      value: false,
    }));
  } catch (error) {
    yield put(aSetOpenModal({
      modal: error.modal || MODALS.GENERAL_ERROR,
      errorMessage: error.modal ? error.message : MODALS.GENERAL_ERROR,
      key: FETCH_OPTIONS.EXPORT_ROUND_SPECIALS_ODDS,
      value: false,
    }));
  }
}

/**
 *
 * @param action
 * @memberof sagas/roundSpecials
 * @async
 * @returns {void}
 */
function* getOddsDetail(action) {
  try {
    yield put(aSetIsFetching({
      key: FETCH_OPTIONS.GET_ROUND_SPECIALS_ODDS_DETAILS,
      value: true,
    }));

    const selectedOddsMarkets = yield select(getRoundSpecialSelectedMarketHistoryOdds);

    const marketIdsQueryParam = selectedOddsMarkets.map(marketId => `markets[]=${marketId}`).join('&');
    const queryParam = marketIdsQueryParam ? `roundId=${action.payload.id}&${marketIdsQueryParam}` : `roundId=${action.payload.id}`;
    const response = yield call(
      requestGet,
      `${API_URLS.GET_ROUND_SPECIAL_ODDS_DETAILS}?${queryParam}`,
      true,
    );

    if (response.status === -1) {
      throw Error(response.error.message);
    }

    yield put(aGetRoundSpecialOddsDetailsSuccessAction({ oddsDetails: response.data.oddsDetails, marketsDisplayed: response.data.marketsDisplayed }));

    yield put(aSetIsFetching({
      key: FETCH_OPTIONS.GET_ROUND_SPECIALS_ODDS_DETAILS,
      value: false,
    }));
  } catch (error) {
    yield put(aSetOpenModal({
      modal: error.modal || MODALS.GENERAL_ERROR,
      errorMessage: error.modal ? error.message : MODALS.GENERAL_ERROR,
      key: FETCH_OPTIONS.GET_ROUND_SPECIALS_ODDS_DETAILS,
      value: false,
    }));
  }
}

/**
 * @memberof sagas/roundSpecials
 * @async
 * @returns {void}
 */
export default function* roundSpecialsSaga() {
  yield takeLatest(aGetRoundSpecialRoundsAction, getRoundSpecialsRoundSaga);
  yield takeLatest(aGetRoundSpecialDetailsAction, getRoundSpecialDetailsSaga);
  yield takeLatest(aExportRoundSpecialOddsDetailsAction, exportExcelRoundSpecial);
  yield takeLatest(aGetRoundSpecialOddsDetailsAction, getOddsDetail);
}

